import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/api/services/auth.service';
import { clientService } from '@/api/services/client.service';
import { storage } from '@/lib/storage';
import { queryKeys } from '../query-keys';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import type { RegisterRequest, LoginRequest, ChangePasswordRequest, User } from '@/api/types/auth.types';

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { refreshAuth } = useAuth();

  return useMutation({
    mutationFn: (data: LoginRequest) => authService.login(data),
    onSuccess: (response) => {
      // Handle both formats: { user, token } or { success: true, data: { user, token } }
      const authData = (response as any).data ? (response as any).data : response;
      
      if (authData.token && authData.user) {
        storage.setToken(authData.token);
        storage.setUser<User>(authData.user);
        queryClient.setQueryData(queryKeys.auth.currentUser(), authData.user);
        
        // Force refresh auth context to update immediately
        refreshAuth();
        
        toast({
          title: t('auth.loggedIn'),
          description: `${t('auth.welcomeBackMessage')} ${authData.user.name}`,
        });

        // Small delay to ensure auth context is updated before navigation
        setTimeout(() => {
          // Redirect based on user type
          const redirectPath = authData.user.type === 'admin' 
            ? '/admin/dashboard' 
            : '/dashboard';
          navigate(redirectPath);
        }, 100);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.message || error?.response?.data?.message || t('common.error') || 'حدث خطأ غير متوقع';
      toast({
        title: t('auth.loginFailed') || 'فشل تسجيل الدخول',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useRegister = (options?: { skipRedirect?: boolean }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: RegisterRequest) => authService.register(data),
    onSuccess: (response, variables, context) => {
      // Handle both formats: { user, token } or { success: true, data: { user, token } }
      const authData = (response as any).data ? (response as any).data : response;
      
      // If token and user are returned, login automatically
      if (authData.token && authData.user) {
        storage.setToken(authData.token);
        storage.setUser<User>(authData.user);
        queryClient.setQueryData(queryKeys.auth.currentUser(), authData.user);
        
        toast({
          title: t('auth.accountCreated'),
          description: `${t('auth.welcomeBackMessage')} ${authData.user.name}`,
        });

        // Only redirect if not skipped
        if (!options?.skipRedirect) {
          const redirectPath = authData.user.type === 'admin' 
            ? '/admin/dashboard' 
            : '/dashboard';
          navigate(redirectPath);
        }
      } else if (authData.user || (response as any).success) {
        // If only user is returned (pending approval), show review message
        toast({
          title: t('auth.accountCreated'),
          description: t('auth.accountReview'),
        });
        // Navigate to login page after successful registration (only if not skipped)
        if (!options?.skipRedirect) {
          setTimeout(() => {
            navigate('/auth');
          }, 1500);
        }
      }
    },
    onError: (error: any) => {
      // Extract error message from API response
      let errorMessage = t('common.error') || 'حدث خطأ غير متوقع';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.errors) {
        // Get first validation error
        const errors = error.response.data.errors;
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey && Array.isArray(errors[firstErrorKey])) {
          errorMessage = errors[firstErrorKey][0];
        } else if (firstErrorKey) {
          errorMessage = errors[firstErrorKey];
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: t('auth.registerFailed') || 'فشل إنشاء الحساب',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Clear all auth data
      storage.clearAuth();
      queryClient.clear();
      
      toast({
        title: 'تم تسجيل الخروج',
        description: 'تم تسجيل الخروج بنجاح',
      });
      
      // Always redirect to login page
      navigate('/auth', { replace: true });
    },
    onError: () => {
      // Even if logout fails, clear local data and redirect
      storage.clearAuth();
      queryClient.clear();
      
      // Always redirect to login page
      navigate('/auth', { replace: true });
    },
  });
};

export const useChangePassword = () => {
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => authService.changePassword(data),
    onSuccess: () => {
      toast({
        title: t('auth.passwordChanged') || 'تم تغيير كلمة المرور',
        description: t('auth.passwordChangedSuccess') || 'تم تغيير كلمة المرور بنجاح',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.message || error?.response?.data?.message || t('common.error') || 'حدث خطأ غير متوقع';
      toast({
        title: t('auth.passwordChangeFailed') || 'فشل تغيير كلمة المرور',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useCurrentUser = () => {
  return useQuery({
    queryKey: queryKeys.auth.currentUser(),
    queryFn: async () => {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        // If user is a client, fetch their profile data to get status
        if (response.data.type === 'client') {
          try {
            const profileResponse = await clientService.getProfile();
            if (profileResponse.success && profileResponse.data) {
              // Merge user data with client profile data
              const userData = {
                ...response.data,
                client: {
                  ...profileResponse.data,
                  user_id: response.data.id // Add the missing user_id field
                }
              } as User;
              storage.setUser<User>(userData);
              return userData;
            }
          } catch (error) {
            // If profile fetch fails, return basic user data
            console.warn('Failed to fetch client profile:', error);
          }
        }
        
        storage.setUser<User>(response.data);
        return response.data;
      }
      throw new Error('Failed to get current user');
    },
    enabled: !!storage.getToken(),
    retry: false,
  });
};
